import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Banknote,
    CreditCard,
    Download,
    MoreHorizontal,
    Search,
    TrendingUp,
    Receipt,
    Wallet,
} from 'lucide-react';
import {
    Bar,
    BarChart,
    CartesianGrid,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from 'recharts';
import toast from 'react-hot-toast';
import { useAuth } from '../../../contexts/AuthContext';
import LoadingSpinner from '../../common/LoadingSpinner';
import { gymDarkCard } from '../gymPageTheme';

function toISODate(d) {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
}

function startOfWeek(d) {
    const x = new Date(d);
    const day = x.getDay();
    const diff = day === 0 ? -6 : 1 - day;
    x.setDate(x.getDate() + diff);
    return x;
}

function formatEuro(n) {
    const v = Number(n);
    if (Number.isNaN(v)) return '—';
    return v.toLocaleString('it-IT', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
}

function formatDateIt(iso) {
    if (!iso) return '—';
    const [y, m, d] = String(iso).slice(0, 10).split('-');
    if (!y || !m || !d) return iso;
    return `${d}/${m}/${y}`;
}

function paymentMethodBadgeClass(method) {
    switch (method) {
        case 'cash':
            return 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200';
        case 'card':
            return 'bg-sky-100 text-sky-800 dark:bg-sky-900/40 dark:text-sky-200';
        case 'other':
            return 'bg-amber-100 text-amber-900 dark:bg-amber-900/40 dark:text-amber-200';
        default:
            return 'bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-200';
    }
}

const PERIODS = ['today', 'week', 'month', 'custom'];

export default function GymMemberRevenue({ isDark }) {
    const { t } = useTranslation();
    const { getAuthenticatedApi } = useAuth();
    const [period, setPeriod] = useState('month');
    const [from, setFrom] = useState(() => toISODate(new Date(new Date().getFullYear(), new Date().getMonth(), 1)));
    const [to, setTo] = useState(() => toISODate(new Date()));
    const [methodFilter, setMethodFilter] = useState('');
    const [search, setSearch] = useState('');
    const [searchDebounced, setSearchDebounced] = useState('');
    const [summary, setSummary] = useState(null);
    const [series, setSeries] = useState([]);
    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(true);
    const [listLoading, setListLoading] = useState(false);
    const limit = 25;

    useEffect(() => {
        const id = setTimeout(() => setSearchDebounced(search.trim()), 300);
        return () => clearTimeout(id);
    }, [search]);

    useEffect(() => {
        const today = new Date();
        if (period === 'today') {
            const d = toISODate(today);
            setFrom(d);
            setTo(d);
        } else if (period === 'week') {
            setFrom(toISODate(startOfWeek(today)));
            setTo(toISODate(today));
        } else if (period === 'month') {
            setFrom(toISODate(new Date(today.getFullYear(), today.getMonth(), 1)));
            setTo(toISODate(today));
        }
    }, [period]);

    const methodLabel = useCallback(
        (method) => {
            if (!method) return t('gym.billing.revenue.methodUnknown');
            return t(`gym.billing.revenue.methods.${method}`, method);
        },
        [t]
    );

    const loadSummary = useCallback(async () => {
        const api = getAuthenticatedApi();
        const res = await api.get('/gym_client_revenue.php', {
            params: { action: 'summary', from, to },
        });
        if (!res.data?.success) {
            throw new Error(res.data?.error || t('gym.billing.revenue.loadError'));
        }
        setSummary(res.data.summary || null);
        setSeries(Array.isArray(res.data.series) ? res.data.series : []);
    }, [from, to, getAuthenticatedApi, t]);

    const loadList = useCallback(async () => {
        const api = getAuthenticatedApi();
        const res = await api.get('/gym_client_revenue.php', {
            params: {
                action: 'list',
                from,
                to,
                page,
                limit,
                ...(methodFilter ? { method: methodFilter } : {}),
                ...(searchDebounced ? { q: searchDebounced } : {}),
            },
        });
        if (!res.data?.success) {
            throw new Error(res.data?.error || t('gym.billing.revenue.loadError'));
        }
        setItems(Array.isArray(res.data.items) ? res.data.items : []);
        setTotal(Number(res.data.total) || 0);
    }, [from, to, page, limit, methodFilter, searchDebounced, getAuthenticatedApi, t]);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                setLoading(true);
                await loadSummary();
            } catch (err) {
                if (!cancelled) {
                    // eslint-disable-next-line no-console
                    console.error(err);
                    toast.error(err.message || t('gym.billing.revenue.loadError'));
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [loadSummary, t]);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                setListLoading(true);
                await loadList();
            } catch (err) {
                if (!cancelled) {
                    // eslint-disable-next-line no-console
                    console.error(err);
                    toast.error(err.message || t('gym.billing.revenue.loadError'));
                }
            } finally {
                if (!cancelled) setListLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [loadList, t]);

    useEffect(() => {
        setPage(1);
    }, [from, to, methodFilter, searchDebounced]);

    const chartData = useMemo(
        () =>
            series.map((row) => ({
                ...row,
                label: formatDateIt(row.date).slice(0, 5),
            })),
        [series]
    );

    const totalPages = Math.max(1, Math.ceil(total / limit));

    const exportCsv = () => {
        if (!items.length) {
            toast.error(t('gym.billing.revenue.exportEmpty'));
            return;
        }
        const header = [
            t('gym.billing.revenue.columns.date'),
            t('gym.billing.revenue.columns.client'),
            t('gym.billing.revenue.columns.subscription'),
            t('gym.billing.revenue.columns.amount'),
            t('gym.billing.revenue.columns.method'),
            t('gym.billing.revenue.columns.notes'),
        ];
        const rows = items.map((row) => [
            row.paid_at || '',
            row.client_name || '',
            row.subscription_name || '',
            String(row.price ?? ''),
            methodLabel(row.payment_method),
            (row.notes || '').replace(/[\r\n]+/g, ' '),
        ]);
        const csv = [header, ...rows]
            .map((line) => line.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(';'))
            .join('\n');
        const blob = new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `incassi_${from}_${to}.csv`;
        a.click();
        URL.revokeObjectURL(url);
    };

    const cardClass = isDark
        ? `${gymDarkCard} border-white/10 text-white`
        : 'bg-white border-gray-200 text-gray-900';

    if (loading && !summary) {
        return (
            <div className="flex justify-center py-16">
                <LoadingSpinner />
            </div>
        );
    }

    const mix = summary?.mix || { cash_pct: 0, card_pct: 0, other_pct: 0 };
    const byMethod = summary?.by_method || {};

    return (
        <section className="space-y-4">
            <div className={`rounded-xl border p-4 sm:p-5 ${cardClass}`}>
                <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
                    <div>
                        <h2 className="text-base font-semibold">{t('gym.billing.revenue.title')}</h2>
                        <p className="text-xs mt-1 opacity-70 max-w-xl">{t('gym.billing.revenue.description')}</p>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                        {PERIODS.map((key) => (
                            <button
                                key={key}
                                type="button"
                                onClick={() => setPeriod(key)}
                                className={`rounded-lg px-3 py-1.5 text-xs font-medium transition-colors ${
                                    period === key
                                        ? 'bg-indigo-600 text-white'
                                        : isDark
                                          ? 'bg-gray-800 text-white/80 hover:bg-gray-700'
                                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                }`}
                            >
                                {t(`gym.billing.revenue.periods.${key}`)}
                            </button>
                        ))}
                    </div>
                </div>
                {period === 'custom' && (
                    <div className="mt-3 flex flex-wrap gap-3">
                        <label className="text-xs opacity-80">
                            {t('gym.billing.revenue.from')}
                            <input
                                type="date"
                                value={from}
                                onChange={(e) => setFrom(e.target.value)}
                                className="mt-1 block rounded-md border border-gray-300 bg-white px-2 py-1.5 text-sm dark:border-gray-700 dark:bg-gray-800"
                            />
                        </label>
                        <label className="text-xs opacity-80">
                            {t('gym.billing.revenue.to')}
                            <input
                                type="date"
                                value={to}
                                onChange={(e) => setTo(e.target.value)}
                                className="mt-1 block rounded-md border border-gray-300 bg-white px-2 py-1.5 text-sm dark:border-gray-700 dark:bg-gray-800"
                            />
                        </label>
                    </div>
                )}
            </div>

            <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
                <KpiCard
                    isDark={isDark}
                    icon={Wallet}
                    label={t('gym.billing.revenue.kpi.revenue')}
                    value={`€${formatEuro(summary?.total_revenue ?? 0)}`}
                />
                <KpiCard
                    isDark={isDark}
                    icon={Receipt}
                    label={t('gym.billing.revenue.kpi.count')}
                    value={String(summary?.payments_count ?? 0)}
                />
                <KpiCard
                    isDark={isDark}
                    icon={TrendingUp}
                    label={t('gym.billing.revenue.kpi.avg')}
                    value={`€${formatEuro(summary?.avg_ticket ?? 0)}`}
                />
                <div className={`rounded-xl border p-4 ${cardClass}`}>
                    <p className="text-xs font-medium uppercase tracking-wide opacity-65">
                        {t('gym.billing.revenue.kpi.mix')}
                    </p>
                    <div className="mt-3 space-y-2 text-sm">
                        <MixRow
                            icon={Banknote}
                            label={methodLabel('cash')}
                            pct={mix.cash_pct}
                            amount={byMethod.cash?.total ?? 0}
                        />
                        <MixRow
                            icon={CreditCard}
                            label={methodLabel('card')}
                            pct={mix.card_pct}
                            amount={byMethod.card?.total ?? 0}
                        />
                        <MixRow
                            icon={MoreHorizontal}
                            label={methodLabel('other')}
                            pct={mix.other_pct}
                            amount={byMethod.other?.total ?? 0}
                        />
                    </div>
                </div>
            </div>

            <div className={`rounded-xl border p-4 sm:p-5 ${cardClass}`}>
                <h3 className="text-sm font-semibold mb-3">{t('gym.billing.revenue.chartTitle')}</h3>
                {chartData.length === 0 ? (
                    <p className="text-sm opacity-60 py-10 text-center">{t('gym.billing.revenue.chartEmpty')}</p>
                ) : (
                    <div className="h-56 w-full">
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={chartData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke={isDark ? '#374151' : '#e5e7eb'} />
                                <XAxis dataKey="label" tick={{ fontSize: 11, fill: isDark ? '#9ca3af' : '#6b7280' }} />
                                <YAxis
                                    tick={{ fontSize: 11, fill: isDark ? '#9ca3af' : '#6b7280' }}
                                    tickFormatter={(v) => `€${v}`}
                                    width={56}
                                />
                                <Tooltip
                                    formatter={(value) => [`€${formatEuro(value)}`, t('gym.billing.revenue.kpi.revenue')]}
                                    labelFormatter={(_, payload) =>
                                        payload?.[0]?.payload?.date
                                            ? formatDateIt(payload[0].payload.date)
                                            : ''
                                    }
                                    contentStyle={{
                                        background: isDark ? '#111827' : '#fff',
                                        border: `1px solid ${isDark ? '#374151' : '#e5e7eb'}`,
                                        borderRadius: 8,
                                    }}
                                />
                                <Bar dataKey="total" fill="#4f46e5" radius={[4, 4, 0, 0]} />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                )}
            </div>

            <div className={`rounded-xl border overflow-hidden ${cardClass}`}>
                <div className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between border-b border-gray-100 dark:border-white/10">
                    <h3 className="text-sm font-semibold">{t('gym.billing.revenue.listTitle')}</h3>
                    <div className="flex flex-wrap items-center gap-2">
                        <div className="relative">
                            <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 opacity-50" />
                            <input
                                type="search"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                placeholder={t('gym.billing.revenue.searchPlaceholder')}
                                className="w-48 rounded-md border border-gray-300 bg-white py-1.5 pl-8 pr-2 text-sm dark:border-gray-700 dark:bg-gray-800"
                            />
                        </div>
                        <select
                            value={methodFilter}
                            onChange={(e) => setMethodFilter(e.target.value)}
                            className="rounded-md border border-gray-300 bg-white px-2 py-1.5 text-sm dark:border-gray-700 dark:bg-gray-800"
                        >
                            <option value="">{t('gym.billing.revenue.filterAllMethods')}</option>
                            <option value="cash">{methodLabel('cash')}</option>
                            <option value="card">{methodLabel('card')}</option>
                            <option value="other">{methodLabel('other')}</option>
                            <option value="unknown">{methodLabel(null)}</option>
                        </select>
                        <button
                            type="button"
                            onClick={exportCsv}
                            className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 px-3 py-1.5 text-sm hover:bg-gray-50 dark:border-gray-700 dark:hover:bg-gray-800"
                        >
                            <Download className="h-4 w-4" />
                            CSV
                        </button>
                    </div>
                </div>

                {listLoading && items.length === 0 ? (
                    <div className="flex justify-center py-12">
                        <LoadingSpinner />
                    </div>
                ) : items.length === 0 ? (
                    <p className="py-12 text-center text-sm opacity-60">{t('gym.billing.revenue.listEmpty')}</p>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="min-w-full text-sm">
                            <thead className={isDark ? 'bg-gray-800/80 text-white/70' : 'bg-gray-50 text-gray-600'}>
                                <tr>
                                    <th className="px-4 py-3 text-left font-medium">{t('gym.billing.revenue.columns.date')}</th>
                                    <th className="px-4 py-3 text-left font-medium">{t('gym.billing.revenue.columns.client')}</th>
                                    <th className="px-4 py-3 text-left font-medium">{t('gym.billing.revenue.columns.subscription')}</th>
                                    <th className="px-4 py-3 text-left font-medium">{t('gym.billing.revenue.columns.amount')}</th>
                                    <th className="px-4 py-3 text-left font-medium">{t('gym.billing.revenue.columns.method')}</th>
                                    <th className="hidden px-4 py-3 text-left font-medium lg:table-cell">
                                        {t('gym.billing.revenue.columns.notes')}
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {items.map((row) => (
                                    <tr
                                        key={row.id}
                                        className={`border-t ${isDark ? 'border-white/10' : 'border-gray-100'}`}
                                    >
                                        <td className="px-4 py-3 whitespace-nowrap">{formatDateIt(row.paid_at)}</td>
                                        <td className="px-4 py-3">
                                            <div className="font-medium">{row.client_name}</div>
                                            {row.client_email && (
                                                <div className="text-xs opacity-55">{row.client_email}</div>
                                            )}
                                        </td>
                                        <td className="px-4 py-3">{row.subscription_name}</td>
                                        <td className="px-4 py-3 tabular-nums font-medium">€{formatEuro(row.price)}</td>
                                        <td className="px-4 py-3">
                                            <span
                                                className={`inline-flex rounded-full px-2 py-0.5 text-xs font-semibold ${paymentMethodBadgeClass(row.payment_method)}`}
                                            >
                                                {methodLabel(row.payment_method)}
                                            </span>
                                        </td>
                                        <td className="hidden px-4 py-3 max-w-xs truncate opacity-70 lg:table-cell">
                                            {row.notes || '—'}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}

                {totalPages > 1 && (
                    <div className="flex items-center justify-between border-t border-gray-100 px-4 py-3 text-sm dark:border-white/10">
                        <span className="opacity-70">
                            {t('gym.billing.revenue.pagination', {
                                page,
                                pages: totalPages,
                                total,
                            })}
                        </span>
                        <div className="flex gap-2">
                            <button
                                type="button"
                                disabled={page <= 1}
                                onClick={() => setPage((p) => Math.max(1, p - 1))}
                                className="rounded-md border px-3 py-1 disabled:opacity-40 dark:border-gray-700"
                            >
                                {t('common.prev', 'Prec.')}
                            </button>
                            <button
                                type="button"
                                disabled={page >= totalPages}
                                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                                className="rounded-md border px-3 py-1 disabled:opacity-40 dark:border-gray-700"
                            >
                                {t('common.next', 'Succ.')}
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </section>
    );
}

function KpiCard({ isDark, icon: Icon, label, value }) {
    return (
        <div
            className={`rounded-xl border p-4 ${
                isDark ? `${gymDarkCard} border-white/10 text-white` : 'bg-white border-gray-200 text-gray-900'
            }`}
        >
            <div className="flex items-start justify-between gap-2">
                <p className="text-xs font-medium uppercase tracking-wide opacity-65">{label}</p>
                <Icon className="h-4 w-4 opacity-50" />
            </div>
            <p className="mt-2 text-2xl font-bold tabular-nums tracking-tight">{value}</p>
        </div>
    );
}

function MixRow({ icon: Icon, label, pct, amount }) {
    return (
        <div className="flex items-center justify-between gap-2">
            <span className="inline-flex items-center gap-1.5">
                <Icon className="h-3.5 w-3.5 opacity-60" />
                {label}
            </span>
            <span className="tabular-nums text-xs opacity-80">
                {pct}% · €{formatEuro(amount)}
            </span>
        </div>
    );
}
